import React from 'react';
import * as Lucide from 'lucide-react';
import { PageNavItem } from '../Page/PageTypes';
import { IconName, ICON_COMPONENT_MAP } from './IconTypes';
import { useSelector } from 'react-redux';
import { RootState } from '../Store/store';
import { NAV_BAR_HEIGHT_PX } from '@mental-mint/shared-utils';
import { playSound, SharedSoundEffect } from '../Sounds/SoundPlayer';


interface NavBarProps {
  items: (PageNavItem & { pageId: string })[];
  activeItemId: string | null;
  onItemClick: (pageId: string) => void;
}

export const NavBar: React.FC<NavBarProps> = ({ 
  items, 
  activeItemId, 
  onItemClick,
}) => {
  const { currentGame } = useSelector((state: RootState) => state.gameSession);
  const game = currentGame!;

  if (items.length === 0) {
    return null;
  }

  const navStyle: React.CSSProperties = {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    height: `${NAV_BAR_HEIGHT_PX}px`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-around',
    padding: '0 1rem',
    gap: '1rem',
    backgroundColor: 'rgba(0, 0, 0, 0.9)',
    backdropFilter: 'blur(10px)',
    borderTop: '1px solid rgba(255, 255, 255, 0.2)',
    zIndex: 100000,
    boxSizing: 'border-box',
    color: '#fff',
  };
  const renderIcon = (iconName: string, isActive: boolean) => {
    const iconProps = {
      size: 24,
      strokeWidth: isActive ? 3 : 2,
      color: isActive ? game.colorPrimary : 'rgba(255, 255, 255, 0.7)'
    };

    // Get the Lucide component name from our mapping
    const lucideComponentName = ICON_COMPONENT_MAP[iconName as IconName] || ICON_COMPONENT_MAP[IconName.Circle];
    
    // Dynamically get the component from Lucide
    const IconComponent = (Lucide as any)[lucideComponentName];
    
    if (IconComponent) {
      return <IconComponent {...iconProps} />;
    }
    
    // Fallback to Circle if component not found
    return <Lucide.Circle {...iconProps} />;
  };

  const renderNavItem = (item: PageNavItem & { pageId: string }) => {
    const isActive = activeItemId === item.pageId;
    
    const itemStyle: React.CSSProperties = {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.25rem',
      padding: '0.25rem',
      cursor: 'pointer',
      borderRadius: '12px',
      backgroundColor: isActive ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
      border: isActive ? `2px solid ${game.colorPrimary}` : '2px solid transparent',
      transition: 'all 0.2s ease',
      color: isActive ? game.colorPrimary : 'rgba(255, 255, 255, 0.7)',
      userSelect: 'none'
    };

    const labelStyle: React.CSSProperties = {
      fontSize: '0.75rem',
      fontWeight: isActive ? 'bold' : 'normal',
      textAlign: 'center'
    };

    return (
      <div
        key={item.pageId}
        style={itemStyle}
        onClick={() => {
          playSound(SharedSoundEffect.MENU_CLICK);
          onItemClick(item.pageId);
        }}
        title={item.label}
      >
        {renderIcon(item.icon, isActive)}
        <span style={labelStyle}>{item.label}</span>
      </div>
    );
  };

  // Render game title for top nav
  const renderGameTitle = () => {
      return (
          <div 
              style={{
                  flexShrink: 1,
                  minWidth: 0,
                  maxWidth: '40%',
                  cursor: 'pointer',
              }}
              onClick={() => {
                  playSound(SharedSoundEffect.MENU_CLICK);
                  window.location.reload();
              }}
          >
              <img 
                  src={game.titleArt} 
                  alt="Game title art" 
                  style={{ 
                      maxWidth: "100%", 
                      maxHeight: `${NAV_BAR_HEIGHT_PX - 10}px` 
                  }} 
              />
          </div>
      );
  };

  return (
    <nav style={navStyle}>
      {renderGameTitle()}
      {items.map(renderNavItem)}
    </nav>
  );
};
